/* The browser's Supabase client: the publishable key only. Everything sensitive runs in database functions
   that check who is asking. */
import { createClient, type SupabaseClient } from '@supabase/supabase-js'

const url = import.meta.env.VITE_SUPABASE_URL as string
const key = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string
export const backendReady = !!(url && key)

export const supa: SupabaseClient = createClient(url || 'https://placeholder.supabase.co', key || 'placeholder', {
  auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: false, storageKey: 'pinnr:auth' },
  realtime: { params: { eventsPerSecond: 20 } },
})

/* Database errors come back as "code:detail" strings raised in SQL; turn them into words. */
export class PinnrError extends Error {
  constructor(public code: string) {
    super(code)
  }
}

export async function call<T>(fn: string, args: Record<string, unknown> = {}): Promise<T> {
  if (!backendReady) throw new PinnrError('offline')
  const { data, error } = await supa.rpc(fn, args)
  if (error) throw new PinnrError(error.message || 'server')
  return data as T
}

export async function api<T>(path: string, body?: unknown, method = 'POST'): Promise<T> {
  const { data } = await supa.auth.getSession()
  const token = data.session?.access_token
  const r = await fetch('/api/' + path, {
    method,
    headers: { 'content-type': 'application/json', ...(token ? { authorization: `Bearer ${token}` } : {}) },
    body: method === 'GET' ? undefined : JSON.stringify(body ?? {}),
  })
  let j: (T & { error?: string }) | null = null
  try {
    j = await r.json()
  } catch {
    /* not json */
  }
  if (!r.ok || !j || j.error) throw new PinnrError(j?.error || (r.status === 404 ? 'offline' : 'server'))
  return j
}

/* The words for each error code, shown in the red bar or under a field. */
export function errorText(e: unknown): string {
  const code = e instanceof PinnrError ? e.code : e instanceof Error ? e.message : String(e)
  const map: Record<string, string> = {
    offline: "Can't reach Pinnr right now. Check your connection.",
    server: 'Something went wrong on our side. Try again.',
    auth: 'Sign in first.',
    banned: 'This account is banned.',
    slow_down: 'Slow down a little and try again in a minute.',
    username_taken: 'That username is taken.',
    bad_username: 'Usernames are 3 to 20 lowercase letters, numbers, _ and .',
    blocked_username: "You can't use that username.",
    blocked_display: "You can't use that name.",
    'blocked:username': "You can't use that username.",
    'blocked:display_name': "You can't use that name.",
    'blocked:bio': "You can't use that bio.",
    'blocked:name': "You can't use that name.",
    'blocked:description': "You can't use that description.",
    'blocked:comment': "You can't post that comment.",
    'blocked:line': "You can't use that line.",
    email_taken: 'That email is already on another account.',
    bad_email: "That email doesn't look right.",
    too_young: 'You need to be 13 or older to make an account.',
    bad_birthday: 'Pick your birth month and year.',
    wrong_login: "That username and password don't match.",
    wrong_password: "That password isn't right.",
    bad_token: 'This link has expired. Ask for a new one.',
    coins: "You don't have enough coins.",
    owned: 'You already own that.',
    premium_only: 'That one comes with Premium.',
    bad_code: "That code doesn't work.",
    used: "You've already used that code.",
    daily_used: "You've already played today's daily.",
    not_yours: "That isn't yours.",
    few: 'A map needs at least 10 spots.',
    many: 'A map can hold up to 2,000 spots.',
    payments_off: "Payments aren't switched on yet.",
    payments: "Couldn't reach the payment service. Try again.",
    adult: 'A parent or guardian needs to make the purchase.',
    not_admin: 'Admins only.',
    admin: 'Admins only.',
    confirm: 'Type your username to confirm.',
    bad_password: 'Type a password.',
    bad_login: 'Type your username and password.',
    no_subscription: "There's no subscription on this account.",
    'bad:display_name': 'Names are 1 to 64 characters.',
    'bad:bio': 'Bios are up to 160 characters.',
    'bad:color': "That color doesn't work.",
    'bad:avatar': "That photo didn't upload. Try again.",
    'bad:item': "That doesn't go there.",
    not_owned: "You don't own that yet.",
    missing: "That isn't here anymore.",
    'bad:slot': "That challenge can't be swapped.",
    none: 'No other challenges left today.',
    not_played: "Play today's daily first.",
    limit: "You've made as many as you can.",
    self: "That's you.",
    blocked: "You can't do that with this player.",
    'bad:mode': 'Something went wrong on our side. Try again.',
    'bad:rounds': 'Something went wrong on our side. Try again.',
    no_profile: 'Sign in again.',
    'bad:name': 'Give it a name of at least 2 characters.',
    'bad:thumb': "That picture didn't upload. Try again.",
  }
  return map[code] || map.server
}
